import { Datetime, Schema } from '@/utils';
import {
  LostAndFoundIssueDocument,
  LostAndFoundIssueReturnStatusEnum,
  LostAndFoundIssueWithCache,
  LostAndFoundIssueWithCacheReturnStatusEnum,
  Trip,
} from '@/models/gen/graphql';
import { clearFormStorage, getFormStorage, setFormStorage } from '@/common/Form';

export namespace LostAndFoundHistoryFormDefinitions {
  export type Locale =
    | 'SUBMIT_BUTTON_TEXT'
    | 'SUBMIT_BUTTON_COLOR'
    | 'City'
    | 'Create Lost and Found'
    | 'Crew Member Email'
    | 'Crew Member Name'
    | 'Crew Member Phone'
    | 'Description'
    | 'Dispatcher'
    | 'Description of Item'
    | 'Driver Name'
    | 'Edit Lost and Found'
    | 'Invalid date format'
    | 'Invalid email format'
    | 'Item Lost'
    | 'Lost and Found'
    | 'Lost and Found History'
    | 'Must be a positive number'
    | 'Must be exactly 2 characters'
    | 'Must be exactly 3 characters'
    | 'Must be exactly 4 characters'
    | 'Must be exactly 5 characters'
    | 'Must not exceed 256 characters'
    | 'Must not exceed 512 characters'
    | 'Must not exceed 64 characters'
    | 'Notes'
    | 'Outbound FLT #'
    | 'Outbound Date/Time'
    | 'Return Status'
    | 'Required'
    | 'State'
    | 'Shipping Address'
    | 'Shipping Cost'
    | 'Shipping Method'
    | 'Tracking Number'
    | 'Upload Files'
    | 'Van Number'
    | 'Zip Code'
    | 'Airport'
    | 'Flight #'
    | 'Additional Crew Supplied Information';
  export type Value = Partial<LostAndFoundIssueWithDocuments>;
  export type Props = {
    onSubmit?: (data: Value) => void | Value | Promise<Value> | undefined;
    onEditLostAndFound?: (data: Value) => void | Value | Promise<Value> | undefined;
    locale?: Partial<Record<Locale, string>>;
    value?: Value;
    trip?: Partial<Trip>;
  };
  export const initValue: Value = {
    id: undefined,
    crewAirportCode: undefined,
    crewFlightNumber: undefined,
    crewDriverName: undefined,
    crewMemberEmail: undefined,
    crewMemberName: '',
    crewMemberPhone: '',
    description: '',
    dispatcherId: null,
    driverId: null,
    itemLost: '',
    notes: '',
    outboundDatetime: '',
    outboundFlightName: undefined,
    returnStatus: LostAndFoundIssueWithCacheReturnStatusEnum.ReportedLost,
    shippingAddress: '',
    shippingCity: '',
    shippingCost: 0,
    shippingMethod: '',
    shippingStateCode: '',
    shippingZipCode: '',
    trackingNumber: '',
    tripId: null,
    vehicleId: null,
  };
  export const getLocale = (value: Value): Partial<Record<Locale, string>> => ({
    SUBMIT_BUTTON_TEXT: value.id ? 'Save Lost and Found' : 'Create Lost and Found',
    SUBMIT_BUTTON_COLOR: value.id ? 'primary' : 'success',
  });
  export const validator = (locale: (original: Locale) => string) =>
    Schema((z) =>
      z.object({
        crewMemberEmail: z.string().email({ message: locale('Invalid email format') }),
        crewMemberName: z
          .string()
          .max(64, { message: locale('Must not exceed 64 characters') })
          .refine((val: string): boolean => val?.trim() !== '', { message: locale('Required') }),
        crewMemberPhone: z.string().refine((val: string): boolean => val?.trim() !== '', { message: locale('Required') }),
        description: z.string().min(1, { message: locale('Required') }),
        dispatcherId: z.string().uuid({ message: 'Required' }),
        driverId: z.string().uuid({ message: 'Required' }),
        itemLost: z
          .string()
          .nonempty({ message: locale('Required') })
          .max(64, { message: locale('Must not exceed 64 characters') }),
        notes: z.string(),
        outboundDatetime: z
          .string()
          .refine((val: string): boolean => !!val && !!new Datetime(val).toString(), { message: locale('Required') }),
        outboundFlightName: z.string().max(64, { message: locale('Must not exceed 64 characters') }),
        returnStatus: z.nativeEnum(LostAndFoundIssueReturnStatusEnum, { message: 'Required' }),
        shippingAddress: z.string().max(256, { message: locale('Must not exceed 256 characters') }),
        shippingCity: z.string().max(64, { message: locale('Must not exceed 64 characters') }),
        shippingCost: z
          .string()
          .refine((val: string = '0'): boolean => parseFloat(val) >= 0, { message: locale('Must be a positive number') }),
        shippingMethod: z.string().max(64, { message: locale('Must not exceed 64 characters') }),
        shippingStateCode: z.string().length(2, { message: locale('Must be exactly 2 characters') }),
        shippingZipCode: z.string().length(5, { message: locale('Must be exactly 5 characters') }),
        trackingNumber: z.string().max(64, { message: locale('Must not exceed 64 characters') }),
        tripId: z.string().uuid({ message: 'Required' }),
        vehicleId: z.string().uuid({ message: 'Required' }),
        crewAirportCode: z.string().length(3, { message: locale('Must be exactly 3 characters') }),
        crewFlightNumber: z.string(),
        crewFlightName: z.string(),
        crewDriverName: z.string().max(64, { message: locale('Must not exceed 64 characters') }),
      })
    );
  export const searchable = {
    tripAirportCode: (val) => val,
    datetime: (val) => val,
    dispatcherFirstName: (val, full) => [val, full?.dispatcherLastName || ''].join(' ').trim(),
    tripServicerIataAirlineCode: (val) => val,
    tripFlightNumber: (val) => val,
    tripLoopName: (val) => val,
    vehicleTrackingId: (val) => val,
    driverFirstName: (val, full) => [val, full?.driverLastName || ''].join(' ').trim(),
    description: (val) => val,
    returnStatus: (val) => val,
  };
  export const saveForm = (values: Value): void => setFormStorage('LostAndFoundHistoryForm', values);
  export const loadForm = (): Value => getFormStorage('LostAndFoundHistoryForm');
  export const clearForm = (): void => clearFormStorage('LostAndFoundHistoryForm');
}

export type LostAndFoundIssueWithDocuments = LostAndFoundIssueWithCache & {
  documents: LostAndFoundIssueDocument[];
};
